import React, { useState, useEffect, useCallback } from 'react';
import VehicleSelect from '../vehicle/VehicleSelect';
import CreditorSelect from './CreditorSelect';
import Fields from './CreditorFields';
import CreditorForm from './CreditorForm';
import PolicyChange from '../PolicyChange';
import ChangedFields from '../ChangedFields';
import Conclusion from '../Conclusion';
import moment from 'moment';
import { statesByAbbreviation } from '../../../../States';

const EditCreditor = ({ actions, policy, effectiveDate, onCancel, onComplete, save, onError, setLoading, onSave, onExit, onClearRate, rate, setRate }) => {
	const [data, setData] = useState({
		changedFields: [],
		creditorType: 0,
		creditorBusinessName: '',
		creditorFirstName: '',
		creditorLastName: '',
		creditorAddressHouseHumber: '',
		creditorAddressStreetName: '',
		creditorAddressApartmentNumber: '',
		creditorAddressPOBox: '',
		creditorAddressCity: '',
		creditorAddressStateId: null,
		creditorAddressZip: '',
		creditorAddressCounty: '',
	});

	const [vehicle, setVehicle] = useState(null);
	const [creditor, setCreditor] = useState(null);
	const [isComplete, setIsComplete] = useState(false);
	const [addressState, setAddressState] = useState({ addressLoading: false });
	const [initialZipCode, setInitialZipCode] = useState(null);

	useEffect(() => {
		if (save) {
			setData({ ...data, ...save });

			if (save.originalVehicle) {
				setVehicle(save.originalVehicle);
			}

			if (save.originalCreditor) {
				setCreditor(save.originalCreditor);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [save]);

	const onChange = useCallback(
		(_, { name, value }) => {
			setData({ ...data, [name]: value, changedFields: [...new Set([...data.changedFields, name])] });
		},
		[data]
	);

	const onAddressChange = useCallback(
		(changedData) => {
			const changedKeys = Object.keys(changedData);
			setData({ ...data, ...changedData, changedFields: [...new Set([...data.changedFields, ...changedKeys])] });
		},
		[data]
	);

	const onSaveAndExit = useCallback(() => {
		onSave({ ...data, originalVehicle: vehicle, originalCreditor: creditor }, rate);
		onExit();
	}, [creditor, data, onExit, onSave, rate, vehicle]);

	const onEdit = useCallback(() => {
		onClearRate();
	}, [onClearRate]);

	const onRate = useCallback(async () => {
		try {
			setLoading('Generating rate');
			const result = await actions.rateCreditor({ policyId: policy.policyId, policyNumber: policy.policyNumber, endorsementPolicyImageNumber: rate?.policyImageNum, vehicleNum: vehicle.vehicleNum, creditorNum: creditor.creditorNum, effectiveDate, ...data });
			
			const _rate = {
				...result.response,
				email: {
					action: 'Edited',
					vehicle,
					addressLine1: data.creditorBusinessName !== '' ? data.creditorBusinessName : `${data.creditorFirstName} ${data.creditorLastName}`,
					addressLine2: (`${data.creditorAddressHouseNumber} ${data.creditorAddressStreetName} ${data.creditorAddressApartmentNumber}`.trim() + (data.creditorAddressPOBox !== '' ? ` P.O. Box ${data.creditorAddressPOBox}` : '')).trim(),
					addressLine3: `${data.creditorAddressCity}, ${Fields.creditorAddressStateId.options()[data.creditorAddressStateId - Fields.creditorAddressStateId.base]} ${data.creditorAddressZip}`,
				},
			};
			setRate(_rate);
			await onSave({ ...data, originalVehicle: vehicle, originalCreditor: creditor }, _rate);
			setLoading(null);
		} catch (error) {
			setLoading(null);
			onError(error.error);
		}
	}, [setLoading, actions, policy, rate, vehicle, creditor, effectiveDate, data, setRate, onSave, onError]);

	const onRateConfirm = useCallback(() => {
		onComplete(rate);
		setIsComplete(true);
	}, [setIsComplete, onComplete, rate]);

	const addressLookup = useCallback(
		async (zipCode) => {
			if (zipCode) {
				// did the zip code change
				if (zipCode !== initialZipCode) {
					if (zipCode && zipCode.length === 5) {
						try {
							setAddressState({ loadingAddress: true });
							const result = await actions.addressLookup(zipCode);

							if (result) {
								const state = statesByAbbreviation[result.response.stateAbbrev];

								if (data.creditorAddressStateId !== state ||
									data.creditorAddressCity !== result.response.city) {


                                    const changedData = {
                                        creditorAddressStateId: state,
                                        creditorAddressCity: result.response.city,
                                    };
                                    onAddressChange(changedData);
                                }
                            }
						} catch (error) {}
						setAddressState({ loadingAddress: false });
					}
					setInitialZipCode(null);
				}
			}
		},
		[actions, initialZipCode, onAddressChange]
	);

	const onCreditorChange = useCallback(
		(creditor) => {
			let creditorZip = creditor.address.zip;
			if (creditorZip.length > 5) {
				creditorZip = creditorZip.substring(0, 5);
			}

			setCreditor(creditor);
			setInitialZipCode(creditorZip);
	
			setData({
				...data,
				creditorType: creditor.businessName !== '' ? 0 : 1,
				creditorBusinessName: creditor.businessName,
				creditorFirstName: creditor.firstName,
				creditorLastName: creditor.lastName,
				creditorAddressHouseNumber: creditor.address.houseNumber,
				creditorAddressApartmentNumber: creditor.address.aptNum,
				creditorAddressStreetName: creditor.address.streetName,
				creditorAddressPOBox: creditor.address.poBox,
				creditorAddressCity: creditor.address.city,
				creditorAddressStateId: creditor.address.stateId,
				creditorAddressZip: creditorZip,
				creditorAddressCounty: creditor.address.county,
			});
		},
		[data]
	);

	if (!vehicle) return <VehicleSelect policy={policy} onVehicleSelect={setVehicle} message="Please select the vehicle you're editing" onCancel={onCancel} minimumCreditorsRequired={1} />;
	if (!creditor) return <CreditorSelect vehicle={vehicle} onCreditorSelect={onCreditorChange} message="Please select the creditor you'd like to edit" onCancel={onCancel} />;

	if (rate && !isComplete) {
		return (
			<PolicyChange name='Creditor' policy={policy} onComplete={onRateConfirm} onSaveAndExit={onSaveAndExit} onEdit={onEdit} onCancel={onCancel} title={`Please review and complete editing the creditor`} showEdit>
				<ChangedFields fields={Fields} changedFields={data.changedFields} data={data} other={[{ label: 'Effective Date of Change', text: `${moment(effectiveDate).format('L')} to ${moment(rate.policyExpirationDate).format('L')}` }]} />
			</PolicyChange>
		);
	}

	if (isComplete) {
		return (
			<Conclusion title='The change to your lienholder information has been completed successfully!' policy={policy} onExit={onExit}>
				<ChangedFields fields={Fields} changedFields={data.changedFields} data={data} other={[{ label: 'Effective Date of Change', text: `${moment(effectiveDate).format('L')} to ${moment(rate.policyExpirationDate).format('L')}` }]} />
			</Conclusion>
		);
	}

	return <CreditorForm data={data} onChange={onChange} vehicle={vehicle} onSaveAndExit={onSaveAndExit} onContinue={onRate} isEdit onCancel={onCancel} addressLookup={addressLookup} addressState={addressState} />;
};

export default EditCreditor;
